import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Star, 
  FileText, 
  TrendingUp, 
  Users, 
  ShoppingCart,
  Calendar,
  DollarSign,
  PieChart,
  BarChart3,
  Download,
  FileDown,
  Search,
  Filter,
  CalendarIcon
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { generatePDFReport } from "@/lib/pdf-generator";
import { DashboardShell } from "@/components/dashboard/dashboard-shell";
interface ReportItem {
  id: string;
  name: string;
  description: string;
  isFavorite?: boolean;
  icon: React.ReactNode;
  category: string;
}

const reportCategories = {
  favorites: {
    title: "Favorites",
    icon: <Star className="h-5 w-5" />,
    reports: [
      {
        id: "accounts-receivable-aging",
        name: "Accounts receivable aging summary",
        description: "See which customers owe you money and for how long",
        isFavorite: true,
        icon: <TrendingUp className="h-4 w-4" />,
        category: "favorites"
      },
      {
        id: "balance-sheet",
        name: "Balance Sheet",
        description: "See your company's financial position",
        isFavorite: true,
        icon: <BarChart3 className="h-4 w-4" />,
        category: "favorites"
      },
      {
        id: "profit-loss",
        name: "Profit and Loss",
        description: "See your company's income and expenses",
        isFavorite: true,
        icon: <DollarSign className="h-4 w-4" />,
        category: "favorites"
      }
    ]
  },
  customReportBuilder: {
    title: "Custom report builder",
    icon: <FileText className="h-5 w-5" />,
    reports: [
      {
        id: "custom-builder",
        name: "Create new report",
        description: "Check out a new way to build and view reports in QuickBooks. Customize these popular reports or start from scratch",
        icon: <PieChart className="h-4 w-4" />,
        category: "custom"
      }
    ]
  },
  businessOverview: {
    title: "Business overview",
    icon: <BarChart3 className="h-5 w-5" />,
    reports: [
      {
        id: "audit-log",
        name: "Audit Log",
        description: "Track changes made to your company",
        icon: <FileText className="h-4 w-4" />,
        category: "business"
      },
      {
        id: "balance-sheet-detailed",
        name: "Balance Sheet",
        description: "Summary of your company's financial position",
        isFavorite: true,
        icon: <BarChart3 className="h-4 w-4" />,
        category: "business"
      },
      {
        id: "balance-sheet-comparison",
        name: "Balance Sheet Comparison",
        description: "Compare balance sheets across periods",
        icon: <TrendingUp className="h-4 w-4" />,
        category: "business"
      },
      {
        id: "balance-sheet-detail",
        name: "Balance Sheet Detail",
        description: "Detailed view of balance sheet accounts",
        icon: <BarChart3 className="h-4 w-4" />,
        category: "business"
      },
      {
        id: "cash-flow-statement",
        name: "Statement of Cash Flows",
        description: "Track cash coming in and going out",
        icon: <DollarSign className="h-4 w-4" />,
        category: "business"
      },
      {
        id: "business-snapshot",
        name: "Business Snapshot",
        description: "Quick overview of key business metrics",
        icon: <PieChart className="h-4 w-4" />,
        category: "business"
      },
      {
        id: "profit-loss-detailed",
        name: "Profit and Loss",
        description: "Your income and expenses",
        isFavorite: true,
        icon: <DollarSign className="h-4 w-4" />,
        category: "business"
      }
    ]
  },
  whoOwesYou: {
    title: "Who owes you",
    icon: <Users className="h-5 w-5" />,
    reports: [
      {
        id: "ar-aging-summary",
        name: "Accounts receivable aging summary",
        description: "See which customers owe you money",
        isFavorite: true,
        icon: <TrendingUp className="h-4 w-4" />,
        category: "receivables"
      },
      {
        id: "ar-aging-detail",
        name: "Accounts receivable aging detail",
        description: "Detailed aging of customer balances",
        icon: <FileText className="h-4 w-4" />,
        category: "receivables"
      },
      {
        id: "collections-report",
        name: "Collections Report",
        description: "Track collection efforts",
        icon: <Users className="h-4 w-4" />,
        category: "receivables"
      },
      {
        id: "customer-balance-summary",
        name: "Customer Balance Summary",
        description: "Summary of customer balances",
        icon: <DollarSign className="h-4 w-4" />,
        category: "receivables"
      },
      {
        id: "invoice-list",
        name: "Invoice List",
        description: "List of all invoices",
        icon: <FileText className="h-4 w-4" />,
        category: "receivables"
      }
    ]
  },
  salesAndCustomers: {
    title: "Sales and customers",
    icon: <ShoppingCart className="h-5 w-5" />,
    reports: [
      {
        id: "sales-by-customer-summary",
        name: "Sales by Customer Summary",
        description: "See which customers buy the most",
        icon: <Users className="h-4 w-4" />,
        category: "sales"
      },
      {
        id: "sales-by-product-summary",
        name: "Sales by Product/Service Summary",
        description: "See which products/services sell the most",
        icon: <ShoppingCart className="h-4 w-4" />,
        category: "sales"
      },
      {
        id: "customer-contact-list",
        name: "Customer Contact List",
        description: "List of customer contact information",
        icon: <Users className="h-4 w-4" />,
        category: "sales"
      },
      {
        id: "income-by-customer",
        name: "Income by Customer Summary",
        description: "See income breakdown by customer",
        icon: <DollarSign className="h-4 w-4" />,
        category: "sales"
      }
    ]
  }
};

export default function ReportsDashboard() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), 0, 1), // Start of current year
    to: new Date() // Today
  });

  const handleGenerateReport = async (reportId: string, format: 'html' | 'pdf' | 'xlsx' = 'html') => {
    try {
      if (format === 'pdf') {
        // Check if this is a Balance Sheet report
        if (reportId.includes('balance')) {
          // Use dedicated Balance Sheet PDF endpoint
          const response = await fetch('/api/balance-sheet/pdf-data', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({}),
          });

          if (response.ok) {
            const reportData = await response.json();
            if (reportData.success && reportData.assets) {
              generatePDFReport(reportData);
            } else {
              console.error('Balance Sheet PDF generation failed:', reportData);
            }
          }
          return;
        }
        
        // Use general PDF endpoint for other reports
        const response = await fetch('/api/reports/pdf-data', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            reportType: reportId.includes('profit') ? 'profit_loss' :
                       reportId.includes('cash') ? 'cash_flow' : 'profit_loss',
            startDate: dateRange.from.toISOString().split('T')[0],
            endDate: dateRange.to.toISOString().split('T')[0]
          }),
        });

        if (response.ok) {
          const reportData = await response.json();
          if (reportData.success) {
            generatePDFReport(reportData);
          } else {
            console.error('PDF generation failed:', reportData);
          }
        }
      } else {
        // Use regular endpoint for HTML and Excel
        const response = await fetch('/api/reports/generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            reportType: reportId.includes('profit') ? 'profit_loss' :
                       reportId.includes('balance') ? 'balance_sheet' :
                       reportId.includes('cash') ? 'cash_flow' : 'profit_loss',
            format: format,
            startDate: dateRange.from.toISOString().split('T')[0],
            endDate: dateRange.to.toISOString().split('T')[0]
          }),
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          const extension = format === 'xlsx' ? 'xlsx' : 'html';
          a.download = `${reportId}.${extension}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      }
    } catch (error) {
      console.error('Error generating report:', error);
    }
  };

  const filteredReports = Object.entries(reportCategories).reduce<Record<string, ReportCategory>>((acc, [key, category]) => {
    const filtered = category.reports.filter(report =>
      report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[key] = { ...category, reports: filtered } as ReportCategory;
    }
    return acc;
  }, {});

  return (
    <DashboardShell>
      <div className="flex-1 space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Reports</h2>
            <p className="text-muted-foreground mt-2">
              Generate comprehensive financial reports for your business
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Date Range Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Report Period:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[280px] justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange?.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, "LLL dd, y")} - {format(dateRange.to, "LLL dd, y")}
                        </>
                      ) : (
                        format(dateRange.from, "LLL dd, y")
                      )
                    ) : (
                      <span>Pick a date range</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <CalendarComponent
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange?.from}
                    selected={dateRange}
                    onSelect={(range) => {
                      if (range?.from && range.to) setDateRange({ from: range.from, to: range.to });
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Quick Date Presets */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setDateRange({
                  from: new Date(new Date().getFullYear(), 0, 1),
                  to: new Date()
                })}
              >
                YTD
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setDateRange({
                  from: new Date(new Date().getFullYear() - 1, 0, 1),
                  to: new Date(new Date().getFullYear() - 1, 11, 31)
                })}
              >
                Last Year
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setDateRange({
                  from: new Date(new Date().setDate(new Date().getDate() - 30)),
                  to: new Date()
                })}
              >
                Last 30 Days
              </Button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search reports..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 max-w-md"
          />
        </div>

        {/* Report Categories */}
        <div className="space-y-8">
          {Object.entries(filteredReports).map(([categoryKey, category]) => (
            <Card key={categoryKey} className="border-none shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  {category.icon}
                  {category.title}
                  <Badge variant="secondary" className="ml-2">
                    {category.reports.length}
                  </Badge>
                </CardTitle>
                {categoryKey === 'customReportBuilder' && (
                  <CardDescription>
                    Check out a new way to build and view reports in ZentroBooks. Customize these popular reports or start from scratch with{" "}
                    <Button variant="link" className="p-0 h-auto text-blue-600">
                      Create new report
                    </Button>
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {category.reports.map((report: ReportItem) => (
                    <div
                      key={report.id}
                      className="p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          {report.isFavorite && (
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          )}
                          {report.icon}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-sm">{report.name}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {report.description}
                          </div>
                        </div>
                      </div>
                      
                      {/* Export Options */}
                      <div className="flex items-center gap-2 pt-3 border-t border-muted">
                        <span className="text-xs text-muted-foreground">Export:</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleGenerateReport(report.id, 'html')}
                          className="h-7 px-2 text-xs"
                        >
                          <FileText className="h-3 w-3 mr-1" />
                          HTML
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleGenerateReport(report.id, 'pdf')}
                          className="h-7 px-2 text-xs"
                        >
                          <FileDown className="h-3 w-3 mr-1" />
                          PDF
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleGenerateReport(report.id, 'xlsx')}
                          className="h-7 px-2 text-xs"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Excel
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardShell>
  );
}

interface ReportCategory {
  title: string;
  icon: React.ReactNode;
  reports: ReportItem[];
}
